/**
 * Formatter date/datetime in italiano.
 * Estratti dai duplicati locali in UploadsPage / CatalogsPage / OrdersPage / AdminPanel.
 *
 * Pattern:
 *   formatDate('2026-05-12T08:30:00Z')  → '12/05/2026, 10:30'
 *   formatDateTime('2026-05-12T08:30:00Z') → '12/05/26, 10:30'
 *
 * Differenza: formatDate usa year:'numeric' (4 cifre), formatDateTime year:'2-digit' (2 cifre).
 * Mantieni entrambi i comportamenti pre-esistenti.
 */

export function formatDate(isoString) {
  if (!isoString) return '—';
  return new Date(isoString).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatDateTime(isoString) {
  if (!isoString) return '—';
  return new Date(isoString).toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

/**
 * Normalizza una data/ora che arriva dal backend SENZA offset
 * ("2026-09-20T08:00:00") in modo che venga letta come UTC.
 *
 * Perché serve: dove il client Mongo non è tz_aware, pymongo restituisce
 * datetime naive e FastAPI li serializza senza offset. `new Date()` su una
 * stringa senza offset la interpreta come ora LOCALE — in Italia CEST — e
 * mostra due ore MENO di quando la cosa è successa davvero.
 *
 * Lo stesso campo può arrivare in entrambe le forme nella stessa sessione
 * (es. POST /nota risponde con un datetime aware, una rilettura restituisce
 * lo stesso valore naive): una stringa che ha già 'Z' o un offset resta
 * intatta, così la conversione non viene mai applicata due volte.
 *
 * Usage:
 *   isoUtc('2026-09-20T08:00:00')       → '2026-09-20T08:00:00Z'
 *   isoUtc('2026-09-20T08:00:00Z')      → '2026-09-20T08:00:00Z'  (invariata)
 *   isoUtc('2026-09-20T10:00:00+02:00') → invariata
 *   isoUtc(null)                        → null   (il chiamante decide)
 */
export function isoUtc(value) {
  if (!value) return value;
  const s = String(value);
  return /(?:Z|z|[+-]\d{2}:?\d{2})$/.test(s) ? s : `${s}Z`;
}

/**
 * Minuti trascorsi da una data/ora del backend (naive UTC, vedi isoUtc sopra)
 * a adesso. Usato per le soglie di eta' dello stato del ponte WhatsApp
 * (StatoWhatsApp.js, HomePage.js): staccato da quanto, codice da quanto.
 *
 * Usage:
 *   minutiDa('2026-09-25T08:00:00')  → minuti da allora a ora
 *   minutiDa(null)                   → null
 */
export function minutiDa(value) {
  if (!value) return null;
  const t = new Date(isoUtc(value)).getTime();
  if (Number.isNaN(t)) return null;
  return (Date.now() - t) / 60000;
}

/**
 * Parsing numerico tollerante all'input italiano: virgola decimale +
 * punto migliaia ("1.234,56" → 1234.56). Equivalente JS di
 * backend/money_utils.parse_eu_amount — usarlo per OGNI input utente di
 * importi/quantità in formato IT (mai un parseFloat/Number nudo con solo
 * .replace(',', '.'): un punto migliaia lo tronca silenziosamente).
 *
 * Usage:
 *   parseNum("1.234,56") → 1234.56
 *   parseNum("12,5")     → 12.5
 *   parseNum(null)       → NaN
 */
export function parseNum(v) {
  if (v === null || v === undefined) return NaN;
  // Già numerico (valori impostati da codice, es. preset rate): passa com'è.
  // Il parsing IT sotto tratterebbe il punto decimale di String(v) come
  // separatore migliaia (1127.28 → 112728).
  if (typeof v === 'number') return v;
  const s = String(v).trim().replace(/\./g, '').replace(',', '.');
  return parseFloat(s);
}

/**
 * Parsing di un campo prezzo mentre l'utente lo DIGITA. Differisce da
 * parseNum: lì il punto è sempre separatore migliaia, qui no — chi scrive un
 * prezzo su tastiera usa spesso il punto come decimale ("12.5"), e trattarlo
 * come migliaia lo trasformerebbe in 125.
 *
 * Regola (la stessa del campo prezzo della cassa, VenditeApp):
 * - c'è una virgola  → è lei il separatore decimale, i punti sono migliaia
 *   ("1.234,56" → 1234.56)
 * - non c'è virgola  → la stringa è già in formato JS ("12.5" → 12.5)
 *
 * Ritorna un numero, oppure '' se il campo è vuoto o non interpretabile —
 * MAI NaN verso lo state di un input controllato. Nota: 0 è un prezzo
 * legittimo, quindi non si può usare `parseFloat(x) || ''` (0 è falsy e
 * verrebbe cancellato: era il bug D1-F2 dell'audit ondata 4).
 *
 * Usage:
 *   parsePriceInput("89,90")    → 89.9
 *   parsePriceInput("1.234,56") → 1234.56
 *   parsePriceInput("12.5")     → 12.5
 *   parsePriceInput("0")        → 0
 *   parsePriceInput("")         → ''
 */
export function parsePriceInput(v) {
  if (v === null || v === undefined) return '';
  if (typeof v === 'number') return Number.isFinite(v) ? v : '';
  const s = String(v).trim();
  if (s === '') return '';
  const normalizzato = s.includes(',')
    ? s.replace(/\./g, '').replace(',', '.')
    : s;
  const n = parseFloat(normalizzato);
  return Number.isNaN(n) ? '' : n;
}

/**
 * Valore da mostrare in un campo prezzo: numero → stringa con virgola
 * decimale, come l'avrebbe scritto l'utente ("89.9" → "89,9").
 */
export function formatPriceInput(v) {
  if (v === null || v === undefined || v === '') return '';
  return String(v).replace('.', ',');
}

/**
 * Formato UNICO degli importi in euro in tutta l'app (decisione owner
 * 2026-11-04): "9.946,00 €".
 * - punto delle migliaia SEMPRE, anche a 4 cifre (in it-IT Intl non raggruppa
 *   sotto 10.000: "9946 €" sotto "11.939 €" si legge male). Il raggruppamento
 *   e' fatto a mano: `useGrouping: 'always'` non e' garantito su Safari iOS.
 * - virgola decimale, centesimi SEMPRE (anche ",00")
 * - simbolo € DOPO, separato da NBSP (U+00A0): l'importo non va mai a capo
 * - segno meno davanti: "-1.234,50 €" (mai "-0,00 €")
 * - null/undefined/''/NaN → `vuoto` (default "—"): ogni chiamante tiene la
 *   sua semantica del vuoto passandola qui.
 *
 * Usage:
 *   formatEur(9946)       → "9.946,00 €"
 *   formatEur(-3.1)       → "-3,10 €"
 *   formatEur(null)       → "—"
 *   formatEur(null, '')   → ""
 */
export function formatEur(value, vuoto = '—') {
  if (value === null || value === undefined || value === '') return vuoto;
  const n = Number(value);
  if (!Number.isFinite(n)) return vuoto;
  const centesimi = Math.round(Math.abs(n) * 100);
  const interi = String(Math.floor(centesimi / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const decimali = String(centesimi % 100).padStart(2, '0');
  const segno = n < 0 && centesimi > 0 ? '-' : '';
  return `${segno}${interi},${decimali}\u00A0€`;
}

/**
 * Prezzo/importo nel formato unico dell'app (vedi formatEur): "12,50 €".
 * Vuoto (null/undefined/NaN) → "0,00 €", come prima.
 */
export function formatPrice(value) {
  return formatEur(value, formatEur(0));
}

/**
 * Canone date (owner 2026-11-04): data «04/11/2026», data con ora
 * «04/11/2026 14:30» in ora italiana, liste recenti «oggi 14:30» / «ieri 09:12».
 * Usare QUESTE nel codice nuovo (formatDate/formatDateTime sopra restano per
 * compatibilità). Le ore del backend senza offset sono UTC: passano da isoUtc.
 */
const TZ = 'Europe/Rome';
const _parti = (d, opts) => Object.fromEntries(
  new Intl.DateTimeFormat('it-IT', { timeZone: TZ, ...opts }).formatToParts(d).map((p) => [p.type, p.value]),
);
const _data = (d) => { const p = _parti(d, { day: '2-digit', month: '2-digit', year: 'numeric' }); return `${p.day}/${p.month}/${p.year}`; };
const _ora = (d) => { const p = _parti(d, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }); return `${p.hour}:${p.minute}`; };
const _istante = (v) => { const d = new Date(isoUtc(v)); return Number.isNaN(d.getTime()) ? null : d; };

export function formatData(value, vuoto = '—') {
  if (!value) return vuoto;
  const s = String(value);
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);   // data pura: niente fuso
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const d = _istante(s);
  return d ? _data(d) : vuoto;
}

export function formatDataOra(value, vuoto = '—') {
  const d = value ? _istante(value) : null;
  return d ? `${_data(d)} ${_ora(d)}` : vuoto;
}

export function formatQuando(value, vuoto = '—', adesso = new Date()) {
  const d = value ? _istante(value) : null;
  if (!d) return vuoto;
  const giorno = _data(d);
  if (giorno === _data(adesso)) return `oggi ${_ora(d)}`;
  if (giorno === _data(new Date(adesso.getTime() - 86400000))) return `ieri ${_ora(d)}`;
  return `${giorno} ${_ora(d)}`;
}
